import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";

import { useMutation, useQuery } from "@tanstack/react-query";

import {
  Button,
  Divider,
  Grid2,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import {
  IconFile,
  IconPlus,
  IconShoppingCart,
  IconTrash,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";

import ContainerLoading from "../components/ContainerLoading";
import { Input } from "../components/Input";
import PageHeader from "../components/PageHeader";
import SelectComponent from "../components/SelectComponent";

import { ordersSettings, registerStoreOrders } from "../services/products";

import {
  formatCpfCnpj,
  formatNumber,
  formatPhone,
  formatPrice,
  maskMoney,
  unMaskMoney,
} from "../helpers/format";
import CustomerRegister from "../components/CustomerRegister";
import Info from "../components/Info";
import moment from "moment";

const OrdersRegister = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState<any>([
    {
      id: crypto.randomUUID(),
      idProduct: "",
      label: "",
      productPrice: 0,
      price: 0,
      qty: 1,
    },
  ]);

  const [paymentType, setPaymentType] = useState("");
  const [installments, setInstallments] = useState("1");
  const [customer, setCustomer] = useState("");
  const [adcionalInfo, setAdcionalInfo] = useState("");
  const [openRegister, setOpenRegister] = useState(false);
  const [paymentsPix, setPaymentsPix] = useState<
    {
      id: number;
      value: number;
      dueDate: string;
    }[]
  >([]);

  const { mutate, isPending } = useMutation({
    mutationFn: registerStoreOrders,
    onSuccess: (e: any) => {
      navigate(`/orders/${e.id}/details`, {
        state: {
          showPix: ["PIX_BILLET", "PIX_INSTALLMENTS"].includes(paymentType),
          showCreditCard: ["CREDIT_CARD"].includes(paymentType),
        },
      });
      toast.success("Pedido criado com sucesso");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) || "Não conseguimos criar o pedido"
      );
    },
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["ordersSettings"],
    queryFn: async () => await ordersSettings(),
    initialData: { products: [], customers: [], taxes: {} },
  });

  const handleUpdateProduct = (
    id: string,
    key: string,
    value: string | number
  ) => {
    if (key === "idProduct") {
      const product = data?.products?.find((p: any) => p.id === value);

      setProducts((prev: any) =>
        prev.map((p: any) =>
          p.id === id
            ? {
                ...p,
                [key]: value,
                productPrice: product.price,
                price: product.price,
              }
            : p
        )
      );
      return;
    }

    if (key === "qty" && Number(value) <= 0) {
      toast.info("Quantidade não pode ser menor que 1");
      return;
    }

    setProducts((prev: any) =>
      prev.map((p: any) => (p.id === id ? { ...p, [key]: value } : p))
    );
  };

  const handleRemoveProduct = (id: string) => {
    setProducts((prev: any) => prev.filter((p: any) => p.id !== id));
  };

  const total = products.reduce((acc: number, item: any) => {
    const price = Number(String(item.price).replace(",", "."));
    return acc + price * item.qty;
  }, 0);

  const discount =
    total -
    products.reduce((acc: number, item: any) => {
      return acc + Number(item.productPrice) * item.qty;
    }, 0);

  const onSubmit = () => {
    if (!customer) {
      toast.info("Por favor selecione um cliente");
      return;
    }

    const hasProduct = products.filter((i: any) => Boolean(i?.idProduct));

    if (isEmpty(hasProduct)) {
      toast.info("Por favor adcione pelo menos um produto");
      return;
    }

    if (total < 1) {
      toast.info("Valor não pode ser menor que R$1,00");
      return;
    }

    if (!customer) {
      toast.info("Por favor selecione um cliente");
      return;
    }

    if (!paymentType) {
      toast.info("Por favor selecione um meio de pagamento");
      return;
    }

    const body = {
      paymentType,
      installments,
      customer,
      products,
      price: total,
      discount,
      adcionalInfo,
      paymentsPix,
    };

    mutate(body);
  };
  const { taxes } = data;

  useEffect(() => {
    if (installments) {
      const payments = Array.from(
        {
          length: Number(installments) || 1,
        },
        (_, id) => ({
          id: id + 1,

          value: total / Number(installments),
          dueDate: moment().add("months", Number(id)).format("YYYY-MM-DD"),
        })
      );
      setPaymentsPix(payments);
    }
  }, [installments, paymentType, total, discount]);

  return (
    <ContainerLoading loading={isPending || isLoading || isFetching}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Novo pedido"
          subtitle="Criei um novo pedido do seu marketplace."
          sticky
          options={
            <Stack
              flexDirection={"row"}
              justifyContent={"flex-end"}
              alignItems={"flex-end"}
            >
              <Button
                variant="contained"
                endIcon={<IconFile />}
                onClick={onSubmit}
              >
                Salvar pedido
              </Button>
            </Stack>
          }
          back
          backRouter="/orders"
        />
        <Grid2 size={12}>
          <Typography variant="subtitle1" color="secondary.contrastText">
            DADOS DO CLIENTE
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Stack
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"flex-end"}
            spacing={2}
            gap={1}
          >
            <SelectComponent
              label="Cliente"
              id="newCliente"
              name="newCliente"
              placeholder="Digite o nome do seu cliente ou telefone "
              value={customer}
              onChange={(i) => {
                setCustomer(i.value);
              }}
              startIcon={<IconUser />}
              options={data?.customers?.map((i: any) => ({
                value: i.id,
                label: `${i.name} - ${formatCpfCnpj(
                  i.document
                )} - ${formatPhone(i.phone)}`,
              }))}
            />
            <Button
              variant="contained"
              sx={{ borderRadius: "8px", height: "42px" }}
              onClick={() => {
                setOpenRegister(true);
              }}
            >
              <IconPlus />
            </Button>
          </Stack>
        </Grid2>
        <Grid2 size={12} marginY={1}>
          <Divider />
        </Grid2>{" "}
        <Grid2 size={12}>
          <Typography variant="subtitle1" color="secondary.contrastText">
            ITENS DO PEDIDO
          </Typography>{" "}
        </Grid2>
        <Grid2
          size={12}
          sx={{
            backgroundColor: "#282828",
            padding: "1rem",
            borderRadius: "1rem",
          }}
        >
          {products.map((item: any) => (
            <Grid2
              container
              key={item.id}
              direction={{ xs: "column", md: "row" }}
              alignItems="flex-start"
              spacing={1}
              mt={1}
            >
              <Grid2 size={{ xs: 12, md: 5 }}>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="subtitle2" textTransform="uppercase">
                    Produto
                  </Typography>
                  <SelectComponent
                    name="productId"
                    placeholder="Digite o nome ou SKU do seu produto"
                    value={item.idProduct}
                    startIcon={<IconShoppingCart />}
                    onChange={(e) => {
                      handleUpdateProduct(item.id, "idProduct", e.value);
                    }}
                    options={data?.products?.map((i: any) => ({
                      value: i.id,
                      label: i.name,
                    }))}
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 2 }}>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="subtitle2" textTransform="uppercase">
                    Quantidade
                  </Typography>
                  <Input
                    value={formatNumber(item.qty)}
                    onChange={(e) =>
                      handleUpdateProduct(
                        item.id,
                        "qty",
                        formatNumber(e.target.value)
                      )
                    }
                    size="small"
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 2 }}>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="subtitle2" textTransform="uppercase">
                    Valor unitário
                  </Typography>
                  <Input
                    value={formatPrice(item.price)}
                    onChange={(e) => {
                      handleUpdateProduct(
                        item.id,
                        "price",
                        maskMoney(e.target.value)
                      );
                    }}
                    size="small"
                  />
                </Stack>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 2 }}>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="subtitle2" textTransform="uppercase">
                    Total
                  </Typography>
                  <Input
                    disabled
                    value={formatPrice(
                      (
                        Number(item.qty) * Number(unMaskMoney(item.price))
                      ).toFixed(2)
                    )}
                    size="small"
                  />
                </Stack>
              </Grid2>

              <Grid2
                size={{ xs: 12, md: 1 }}
                display="flex"
                alignItems="center"
                justifyContent={{ xs: "flex-start", md: "center" }}
                sx={{ marginTop: "7px" }}
              >
                <IconButton
                  onClick={() => handleRemoveProduct(item.id)}
                  color="error"
                  sx={{ mt: { xs: 1, md: 2 } }}
                >
                  <IconTrash />
                </IconButton>
              </Grid2>
            </Grid2>
          ))}
          <Stack
            flexDirection={"row"}
            alignItems={"flex-end"}
            justifyContent={"flex-end"}
            mt={2}
          >
            <Button
              color="success"
              onClick={() => {
                setProducts((i: any) => [
                  ...i,
                  {
                    id: crypto.randomUUID(),
                    idProduct: "",
                    label: "",
                    price: 0,
                    qty: 1,
                  },
                ]);
              }}
            >
              Adcionar produto
            </Button>
          </Stack>
        </Grid2>
        <Grid2
          size={12}
          direction="row"
          alignItems="center"
          sx={{
            backgroundColor: "#282828",
            padding: "1rem",
            borderRadius: "1rem",
          }}
        >
          <Stack flexDirection={"row"} justifyContent={"space-between"}>
            <Typography variant="subtitle1" fontWeight={700}>
              Desconto
            </Typography>
            <Typography variant="h6" color="error" fontWeight={700}>
              {formatPrice(discount < 0 ? discount : 0)}
            </Typography>
          </Stack>
          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            mt="10px"
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Total
            </Typography>
            <Typography variant="h6" color="success" fontWeight={700}>
              {formatPrice(total)}
            </Typography>
          </Stack>
        </Grid2>
        <Grid2 size={12} marginY={2}>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="subtitle1" color="secondary.contrastText">
            PAGAMENTO
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <SelectComponent
            label="Tipo de pagamento"
            placeholder="Escolha o tipo de pagamento"
            value={paymentType}
            onChange={(e) => {
              if (e.value === "PIX_BILLET") {
                setInstallments("1");
              }

              setPaymentType(e.value);
            }}
            startIcon={<IconWallet />}
            options={[
              { value: "MONEY", label: "Dinheiro" },
              { value: "PIX_BILLET", label: "PIX à vista" },
              { value: "PIX_INSTALLMENTS", label: "PIX Parcelado" },
              {
                value: "CREDIT_CARD",
                label: `Cartão de crédito até ${
                  taxes?.installmentsCreditCard || 1
                }X`,
              },
            ]}
          />
        </Grid2>
        {paymentType === "PIX_INSTALLMENTS" && (
          <Grid2 size={12}>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{
                backgroundColor: "#282828",
                padding: "1rem 0rem 1rem 1rem",
                borderRadius: "1rem",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                PIX Parcelado
              </Typography>
              <Stack width={"320px"}>
                <SelectComponent
                  name="productId"
                  placeholder="Parcelas"
                  value={installments}
                  onChange={(e: any) => {
                    setInstallments(e.value);
                  }}
                  options={Array.from(
                    {
                      length: taxes?.installmentsCreditCard || 1,
                    },
                    (_, i) => i + 1
                  )
                    .filter((i) => total / Number(i) >= 10)
                    .map((i) => ({
                      value: String(i),
                      label: `Até ${i}x de ${formatPrice(total / i)}`,
                    }))}
                />
              </Stack>
            </Stack>
          </Grid2>
        )}
        <Grid2 container spacing={1} size={12}>
          {installments &&
            total > 0 &&
            paymentType.includes("PIX") &&
            paymentsPix?.map((i: any, index: number) => (
              <Grid2 size={12}>
                <Stack
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{
                    backgroundColor: "#282828",
                    padding: "1rem",
                    borderRadius: "1rem",
                  }}
                >
                  {" "}
                  <Info
                    size="auto"
                    title="Parcela"
                    subTitle={`${i.id}/${Number(installments)}`}
                  />
                  <Stack>
                    <Input
                      id="birthDate"
                      name="birthDate"
                      label=""
                      type="date"
                      fullWidth
                      value={i.dueDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setPaymentsPix((prev: any) =>
                          prev.map((item: any, idx: any) =>
                            idx === index ? { ...item, dueDate: newDate } : item
                          )
                        );
                      }}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.showPicker()
                      }
                    />
                  </Stack>
                  <Info
                    size="auto"
                    title="Valor"
                    subTitle={formatPrice(i.value)}
                  />
                </Stack>
              </Grid2>
            ))}
        </Grid2>
        {paymentType === "CREDIT_CARD" && (
          <Grid2 size={12}>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{
                backgroundColor: "#282828",
                padding: "1rem 0rem 1rem 1rem",
                borderRadius: "1rem",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Cartão de crédito
              </Typography>{" "}
              <Stack width={"320px"}>
                <SelectComponent
                  name="productId"
                  placeholder="Parcelas"
                  value={installments}
                  onChange={(e: any) => {
                    setInstallments(e.value);
                  }}
                  options={Array.from(
                    {
                      length: taxes?.installmentsCreditCard || 1,
                    },
                    (_, i) => i + 1
                  )
                    .filter((i) => total / Number(i) >= 10)
                    .map((i) => ({
                      value: String(i),
                      label: `Até ${i}x de ${formatPrice(total / i)}`,
                    }))}
                />
              </Stack>
            </Stack>
          </Grid2>
        )}
        <Grid2 size={12} marginY={2}>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="subtitle1" color="secondary.contrastText">
            INFORMAÇÂO ADCIONAL ( Opcional )
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Input
            multiline
            rows={5}
            value={adcionalInfo}
            onChange={(e) => setAdcionalInfo(e.target.value)}
          />
        </Grid2>
      </Grid2>

      <CustomerRegister
        open={openRegister}
        onClose={() => {
          setOpenRegister(false);
        }}
        onAction={(e) => {
          setCustomer(e);
        }}
        refetch={refetch}
      />
    </ContainerLoading>
  );
};

export default OrdersRegister;
